import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { TriangleAlert } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { CreativePreviewFrame } from './CreativePreviewFrame'
import {
  CreativeLivePreviewForm,
  previewLayoutParameters,
  previewSize,
  useCreativePreviewAssets,
} from './CreativeLivePreviewForm'
import { PreviewApi } from '@/services/creatives/previewApi'
import { apiErrorDescription } from '@/services/core/apiError'
import { AuthService } from '@/services/core/auth'
import type { Creative } from '@/services/creatives/creativesApi'

interface CreativePreviewDialogProps {
  creative: Creative | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreativePreviewDialog({
  creative,
  open,
  onOpenChange,
}: CreativePreviewDialogProps) {
  const { t } = useTranslation()
  const enabled = open && !!creative && AuthService.isAuthenticated()
  const { data: assets = [] } = useCreativePreviewAssets(creative, enabled)

  const {
    data: html,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['creative-preview', creative?.id, assets.length],
    queryFn: async () => {
      const { width, height } = previewSize(creative!)
      try {
        // The saved creative renders exactly as it would be delivered
        return await PreviewApi.getCreativeHtml(creative!.id)
      } catch {
        // A creative that cannot serve yet still previews from its layout parameters
        return await PreviewApi.render({
          layoutId: creative!.layoutId,
          lineItemId: creative!.lineItemId,
          creativeWidth: width,
          creativeHeight: height,
          layoutParameters: previewLayoutParameters(creative!, assets),
        })
      }
    },
    enabled,
    retry: false,
  })

  const { width, height } = creative ? previewSize(creative) : { width: 300, height: 300 }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[min(1000px,92vw)]">
        <DialogHeader>
          <DialogTitle>{creative?.name ?? t('creatives.preview')}</DialogTitle>
          <DialogDescription>
            {width}×{height} · {creative?.layoutName}
          </DialogDescription>
        </DialogHeader>

        {creative?.blocksAdServing && (
          <Alert variant="destructive">
            <TriangleAlert className="size-4" />
            <AlertDescription>{t('creatives.previewNotDeliverable')}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="preview">
          <TabsList>
            <TabsTrigger value="preview">{t('creatives.preview')}</TabsTrigger>
            <TabsTrigger value="live">{t('creatives.livePreview')}</TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="pt-4">
            {isLoading && <Skeleton className="h-64 w-full" />}
            {error && (
              <Alert variant="destructive">
                <TriangleAlert className="size-4" />
                <AlertDescription>{apiErrorDescription(error)}</AlertDescription>
              </Alert>
            )}
            {html && (
              <CreativePreviewFrame html={html} width={width} height={height} maxHeight={520} />
            )}
          </TabsContent>

          <TabsContent value="live" className="pt-4">
            {creative && <CreativeLivePreviewForm creative={creative} enabled={enabled} />}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
